import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, ChevronLeft, Loader2, Upload, Info } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { format, parse, isValid } from "date-fns";
import { cn } from "@/lib/utils";
import { getChildBirthdayRange } from "@shared/utils";
import type { ChildProfile } from "@shared/schema";
import Navigation from "@/components/Navigation";
import Footer from "@/components/ui/footer";

interface BirthdayPickerProps {
  value: Date | null | undefined;
  onChange: (date: Date | null | undefined) => void;
  birthdayRange: { minDate: Date; maxDate: Date };
}

function BirthdayPicker({ value, onChange, birthdayRange }: BirthdayPickerProps) {
  const [tempDate, setTempDate] = useState<Date | undefined>(value || undefined);
  const [dateInput, setDateInput] = useState(value ? format(value, "MM/dd/yyyy") : "");
  const [open, setOpen] = useState(false);
  const [inputError, setInputError] = useState("");

  // Sync with form value changes (e.g., when editing existing profile)
  useEffect(() => {
    if (value) {
      setDateInput(format(value, "MM/dd/yyyy"));
      setTempDate(value);
    } else {
      setDateInput("");
      setTempDate(undefined);
    }
  }, [value]);

  const handleDateInputChange = (inputValue: string) => {
    setDateInput(inputValue);
    setInputError("");
    
    // Handle manual clearing
    if (inputValue.trim() === "") {
      onChange(null);
      setTempDate(undefined);
      return;
    }
    
    // Strict validation: only accept exact MM/DD/YYYY format
    if (inputValue.length === 10) {
      try {
        const parsedDate = parse(inputValue, "MM/dd/yyyy", new Date());
        
        // Verify the date is valid and matches the input (prevents "02/30/2020" → March 1st)
        if (isValid(parsedDate) && format(parsedDate, "MM/dd/yyyy") === inputValue) {
          // Check if date is within valid range
          if (parsedDate <= new Date() && parsedDate >= birthdayRange.minDate) {
            setTempDate(parsedDate);
            onChange(parsedDate);
            setInputError("");
          } else {
            setInputError("Date must be between valid child age range");
          }
        } else {
          setInputError("Invalid date format. Use MM/DD/YYYY");
        }
      } catch {
        setInputError("Invalid date format. Use MM/DD/YYYY");
      }
    }
  };

  const handleConfirm = () => {
    if (tempDate) {
      onChange(tempDate);
      setDateInput(format(tempDate, "MM/dd/yyyy"));
      setInputError("");
    }
    setOpen(false);
  };


  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="flex-1">
          <Input
            placeholder="MM/DD/YYYY"
            value={dateInput}
            onChange={(e) => handleDateInputChange(e.target.value)}
            className="bg-white"
            data-testid="input-birthday"
          />
          {inputError && (
            <p className="text-sm text-red-500 mt-1">{inputError}</p>
          )}
        </div>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="bg-white"
              data-testid="button-calendar-picker"
            >
              <CalendarIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent 
            className="w-auto p-0 bg-white" 
            align="end" 
            side="bottom"
            sideOffset={4}
            style={{ maxWidth: 'min(340px, calc(100vw - 2rem))' }}
          >
            <div className="p-2 sm:p-3">
              <Calendar
                mode="single"
                selected={tempDate}
                onSelect={setTempDate}
                defaultMonth={tempDate || value || undefined}
                disabled={(date) =>
                  date > new Date() || date < birthdayRange.minDate
                }
                initialFocus
                captionLayout="dropdown"
                fromYear={birthdayRange.minDate.getFullYear()}
                toYear={birthdayRange.maxDate.getFullYear()}
                data-testid="calendar-birthday"
                className="bg-white"
                classNames={{
                  caption_label: "hidden",
                  caption_dropdowns: "flex gap-1 justify-center mb-2",
                  dropdown_month: "px-2 py-1 border rounded-md bg-white text-xs sm:text-sm",
                  dropdown_year: "px-2 py-1 border rounded-md bg-white text-xs sm:text-sm",
                  dropdown: "px-2 py-1 border rounded-md bg-white text-xs sm:text-sm",
                  months: "flex flex-col",
                  month: "space-y-2",
                  table: "w-full border-collapse",
                  head_cell: "text-muted-foreground w-8 font-normal text-xs",
                  cell: "h-8 w-8 text-center text-sm p-0",
                  day: "h-8 w-8 p-0 font-normal text-sm",
                  day_selected: "bg-purple-600 text-white hover:bg-purple-700 hover:text-white focus:bg-purple-600 focus:text-white",
                }}
              />
            </div>
            <div className="p-2 sm:p-3 border-t">
              <Button
                onClick={handleConfirm}
                disabled={!tempDate}
                className="w-full bg-gradient-to-r from-purple-600 to-teal-600 text-sm"
                data-testid="button-confirm-date"
              >
                Confirm
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

const childProfileSchema = z.object({
  name: z.string().min(1, "Name is required"),
  birthday: z.date({ required_error: "Birthday is required" }),
  gender: z.enum(["girl", "boy", "other"], { required_error: "Gender is required" }),
  preferredLanguage: z.string().optional(),
});

type ChildProfileFormData = z.infer<typeof childProfileSchema>;

function ChildProfileEdit() {
  const [, navigate] = useLocation();
  const [match, params] = useRoute("/child-profiles/:id");
  const { toast } = useToast();
  const isNew = params?.id === "new";
  const profileId = !isNew && params?.id ? parseInt(params.id) : null;

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedAvatarUrl, setGeneratedAvatarUrl] = useState<string | null>(null);
  const [originalPhotoUrl, setOriginalPhotoUrl] = useState<string | null>(null);

  const { data: profile, isLoading } = useQuery<ChildProfile>({
    queryKey: ['/api/child-profiles', profileId],
    enabled: !!profileId,
  });

  const form = useForm<ChildProfileFormData>({
    resolver: zodResolver(childProfileSchema),
    defaultValues: {
      name: "",
      birthday: undefined as any,
      gender: undefined as any,
      preferredLanguage: "en",
    },
    values: profile ? {
      name: profile.name,
      birthday: profile.birthday ? new Date(profile.birthday) : undefined as any,
      gender: (profile.gender === "girl" || profile.gender === "boy" || profile.gender === "other") ? profile.gender : undefined as any,
      preferredLanguage: profile.preferredLanguage || "en",
    } : undefined,
  });

  // Check for successful slot purchase
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const purchased = params.get('purchased');
    
    if (purchased === 'true') {
      toast({
        title: "Payment successful!",
        description: "Now complete your child's profile information below.",
      });
      // Clean up URL
      window.history.replaceState({}, '', '/child-profiles/new');
    }
  }, [toast]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if child name is provided
    const childName = form.getValues('name');
    if (!childName || childName.trim() === '') {
      toast({
        title: "Name Required",
        description: "Please enter the child's name before uploading a photo.",
        variant: "destructive",
      });
      event.target.value = '';
      return;
    }

    setSelectedFile(file);
    
    // Create preview URL
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    // Start generation
    setIsGenerating(true);

    try {
      // Convert file to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Check if the base64 data is too large
      if (base64.length > 10 * 1024 * 1024) {
        throw new Error('Image file is too large. Please try with a smaller image (max 10MB).');
      }

      // Call character generation API
      const response = await apiRequest('POST', '/api/process', {
        characterName: childName.trim(),
        base64Image: base64,
        prompt: `Create a cartoon character named ${childName.trim()} for a children's storybook.`
      }, {
        timeout: 120000 // 2 minutes timeout
      });

      let result;
      try {
        result = await response.json();
      } catch (jsonError) {
        throw new Error(`Server returned invalid response: ${response.status} ${response.statusText}`);
      }

      if (!response.ok) {
        const errorMessage = result?.error || result?.message || `Server error: ${response.status}`;
        throw new Error(errorMessage);
      }

      // Store generated avatar URLs
      if (result.character) {
        setGeneratedAvatarUrl(result.character.cartoonImageUrl);
        setOriginalPhotoUrl(result.character.originalPhotoUrl);
        
        toast({
          title: "Avatar Generated!",
          description: "Your cartoon avatar has been successfully created.",
        });
      }
    } catch (error: any) {
      console.error("Avatar generation error:", error);
      
      let errorMessage = "Failed to generate cartoon avatar. Please try again.";
      
      if (error.message) {
        if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          errorMessage = "Please log in to generate avatars.";
        } else if (error.message.includes('402') || error.message.includes('subscription')) {
          errorMessage = "Premium subscription required for avatar generation.";
        } else if (error.message.includes('413') || error.message.includes('Request Entity Too Large')) {
          errorMessage = "Image file is too large. Please try with a smaller image.";
        } else if (error.message.includes('timeout')) {
          errorMessage = "Request timed out. Please check your connection and try again.";
        } else if (error.message.includes('DOMException')) {
          errorMessage = "Network error occurred. Please check your connection and try again.";
        } else if (error.message) {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });

      // Reset on error
      setSelectedFile(null);
      setPreviewUrl(null);
      setGeneratedAvatarUrl(null);
      setOriginalPhotoUrl(null);
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const saveMutation = useMutation({
    mutationFn: async (data: ChildProfileFormData) => {
      const payload = {
        name: data.name,
        birthday: data.birthday.toISOString(),
        gender: data.gender,
        preferredLanguage: data.preferredLanguage || "en",
        ...(generatedAvatarUrl && { avatarUrl: generatedAvatarUrl }),
        ...(originalPhotoUrl && { originalPhotoUrl: originalPhotoUrl }),
      };

      if (isNew) {
        const response = await apiRequest('POST', '/api/child-profiles', payload);
        
        // Check if payment is required (402 status)
        if (response.status === 402) {
          const data = await response.json();
          // Redirect to purchase flow
          const purchaseResponse = await apiRequest('POST', '/api/child-profiles/purchase', payload);
          const purchaseData = await purchaseResponse.json();
          
          if (purchaseData.url) {
            // Redirect to Stripe checkout
            window.location.href = purchaseData.url;
            return null; // Prevent normal success flow
          }
          throw new Error('Failed to initiate payment');
        }
        
        return response.json();
      } else {
        const response = await apiRequest('PATCH', `/api/child-profiles/${profileId}`, payload);
        return response.json();
      }
    },
    onSuccess: (data) => {
      if (data === null) return; // Skip if redirecting to payment
      
      queryClient.invalidateQueries({ queryKey: ['/api/child-profiles'] });
      toast({
        title: isNew ? "Child profile created" : "Child profile updated",
        description: isNew 
          ? "The child profile has been successfully created." 
          : "The child profile has been successfully updated.",
      });
      navigate("/child-profiles");
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || `Failed to ${isNew ? 'create' : 'update'} child profile.`,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ChildProfileFormData) => {
    saveMutation.mutate(data);
  };

  if (!isNew && isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
        <Navigation />
        <div className="container mx-auto py-8">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const birthdayRange = getChildBirthdayRange();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
      <Navigation />
      <div className="container mx-auto py-8 px-4 max-w-2xl">
      <Button
        variant="ghost"
        size="sm"
        className="mb-4"
        onClick={() => navigate("/child-profiles")}
        data-testid="button-back"
      >
        <ChevronLeft className="h-4 w-4 mr-1" />
        Back to Children
      </Button>

      <Card>
        <CardHeader>
          <CardTitle data-testid="text-form-title">
            {isNew ? "Add Child Profile" : "Edit Child Profile"}
          </CardTitle>
          <CardDescription data-testid="text-form-description">
            {isNew 
              ? "Create a profile for your child to personalize their stories" 
              : "Update your child's profile information"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Child's Name</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Enter child's name" 
                        {...field} 
                        data-testid="input-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Controller
                control={form.control}
                name="birthday"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Birthday</FormLabel>
                    <FormControl>
                      <BirthdayPicker 
                        value={field.value}
                        onChange={field.onChange}
                        birthdayRange={birthdayRange}
                      />
                    </FormControl>
                    <FormDescription>
                      The child's age will be automatically calculated from their birthday
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="gender"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Gender</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      value={field.value || undefined}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-gender" className="bg-white">
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-white">
                        <SelectItem value="girl" data-testid="option-girl">Girl</SelectItem>
                        <SelectItem value="boy" data-testid="option-boy">Boy</SelectItem>
                        <SelectItem value="other" data-testid="option-other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="preferredLanguage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Preferred Story Language</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-language" className="bg-white">
                          <SelectValue placeholder="Select language" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-white">
                        <SelectItem value="en" data-testid="option-en">English</SelectItem>
                        <SelectItem value="es" data-testid="option-es">Spanish</SelectItem>
                        <SelectItem value="fr" data-testid="option-fr">French</SelectItem>
                        <SelectItem value="de" data-testid="option-de">German</SelectItem>
                        <SelectItem value="zh" data-testid="option-zh">Chinese</SelectItem>
                        <SelectItem value="ja" data-testid="option-ja">Japanese</SelectItem>
                        <SelectItem value="pt" data-testid="option-pt">Portuguese</SelectItem>
                        <SelectItem value="it" data-testid="option-it">Italian</SelectItem>
                        <SelectItem value="ru" data-testid="option-ru">Russian</SelectItem>
                        <SelectItem value="ar" data-testid="option-ar">Arabic</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Stories will be generated in this language by default
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <FormLabel>Photo Upload & Cartoon Avatar</FormLabel>
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-5 w-5 p-0 hover:bg-transparent"
                        aria-label="Avatar generation information"
                      >
                        <Info className="h-4 w-4 text-purple-600" />
                      </Button>
                    </HoverCardTrigger>
                    <HoverCardContent className="w-80 bg-white dark:bg-gray-800">
                      <p className="text-sm">Cartoon avatar generation may take a few minutes. Please be patient while our AI creates your character.</p>
                    </HoverCardContent>
                  </HoverCard>
                </div>
                <FormDescription>
                  Upload a photo to generate a cartoon character avatar (optional)
                </FormDescription>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      disabled={isGenerating}
                      className="bg-white"
                      data-testid="input-photo-upload"
                    />
                    <Upload className="h-5 w-5 text-purple-600" />
                  </div>

                  {isGenerating && (
                    <div className="flex items-center gap-2 p-4 bg-purple-50 rounded-lg" data-testid="status-generating">
                      <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
                      <span className="text-sm text-purple-700">Generating cartoon avatar...</span>
                    </div>
                  )}

                  {previewUrl && !generatedAvatarUrl && !isGenerating && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Original Photo:</p>
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="w-32 h-32 object-cover rounded-lg border"
                        data-testid="img-preview"
                      />
                    </div>
                  )}

                  {generatedAvatarUrl && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Generated Cartoon Avatar:</p>
                      <img
                        src={generatedAvatarUrl}
                        alt="Generated cartoon avatar"
                        className="w-32 h-32 object-cover rounded-lg border"
                        data-testid="img-generated-avatar"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/child-profiles")}
                  className="flex-1 bg-white"
                  data-testid="button-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-purple-600 to-teal-600"
                  disabled={saveMutation.isPending}
                  data-testid="button-save"
                >
                  {saveMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isNew ? "Create Profile" : "Save Changes"}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
      </div>
      <Footer />
    </div>
  );
}

export default ChildProfileEdit;
